import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Notice, PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { User } from '../../../models/user';
import { nowOperationalDateTimeString, operationalMoment } from '../../../utils/operationalDateTime';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { apiErrorMessage } from '../../inventory/shared/inventoryApi';
import { officeAllowsProductLend } from '../../inventory/shared/PricingModeField';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, FormActions } from '../shared/AdminUi';
import RouteFormSections, { EMPTY_ROUTE, RouteFormErrors, RouteFormValues, validateRoute } from './RouteFormSections';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

/** Crear ruta: sale llena con la configuración general de rutas de la empresa (se puede cambiar). */
const RouteNew: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { canCreate } = useResourcePermissions('/admin/routes');
  const [values, setValues] = useState<RouteFormValues>(EMPTY_ROUTE);
  const [errors, setErrors] = useState<RouteFormErrors>({});
  const [users, setUsers] = useState<User[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  // ¿El formulario salió lleno con la configuración de rutas de la empresa?
  const [inherited, setInherited] = useState(false);
  const [saving, setSaving] = useState(false);
  const productOffice = officeAllowsProductLend(offices, values.officeId);

  setPageTitle('routes.newTitle');
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [employeeResponse, officesResponse] = await Promise.all([axios.get('sales-reps/unassigned'), axios.get('offices/all')]);
        setUsers(Array.isArray(employeeResponse.data?.data) ? employeeResponse.data.data : []);
        setOffices(Array.isArray(officesResponse.data?.data) ? officesResponse.data.data : []);
        // Configuración general de rutas de la empresa: llena el formulario (se puede cambiar).
        try {
          const { data: defaults } = await axios.get('route-defaults');
          const d = defaults?.settings || {};
          setValues((v) => ({
            ...v,
            creditLimit: Number(d.creditLimit) > 0 ? String(d.creditLimit) : v.creditLimit,
            discountDays: String(d.discountDays ?? 0),
            insurance: String(d.insurance ?? 0),
            insuranceRate: String(d.insuranceRate ?? 0),
            manualInsurance: Boolean(d.manualInsurance),
            allowClientCreditLimit: Boolean(d.allowClientCreditLimit),
            blockNewLoanAfterInstallments: d.blockNewLoanAfterInstallments ? String(d.blockNewLoanAfterInstallments) : '',
            requireExpensePhoto: Boolean(d.requireExpensePhoto),
            maxDiscountPerDay: Number(d.maxDiscountPerDay) > 0 ? String(d.maxDiscountPerDay) : '',
            maxRefinancesPerDay: Number(d.maxRefinancesPerDay) > 0 ? String(d.maxRefinancesPerDay) : '',
            maxExpensesPerDay: Number(d.maxExpensesPerDay) > 0 ? String(d.maxExpensesPerDay) : '',
          }));
          setInherited(true);
        } catch {
          /* Sin configuración de la empresa: valores de siempre. */
        }
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!canCreate) return;
    const found = validateRoute(values, t('common.required'));
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('routes.missingFields'));
      return;
    }
    const v = values;
    setSaving(true);
    try {
      await axios.post('/routes', {
        currentDate: nowOperationalDateTimeString(),
        name: v.name,
        startDate: operationalMoment().toDate(),
        officeId: Number(v.officeId),
        userId: Number(v.userId),
        creditLimit: v.creditLimit,
        discountDays: v.discountDays,
        insurance: v.insurance,
        insuranceRate: v.insuranceRate,
        manualInsurance: v.manualInsurance,
        // Horario, eliminar abonos y préstamos, dispositivos, etc. los toma el API de la configuración de la empresa.
        requireExpensePhoto: v.requireExpensePhoto,
        maxDiscountPerDay: Number(v.maxDiscountPerDay) > 0 ? Number(v.maxDiscountPerDay) : null,
        maxRefinancesPerDay: Number(v.maxRefinancesPerDay) > 0 ? Number(v.maxRefinancesPerDay) : null,
        maxExpensesPerDay: Number(v.maxExpensesPerDay) > 0 ? Number(v.maxExpensesPerDay) : null,
        allowClientCreditLimit: v.allowClientCreditLimit,
        blockNewLoanAfterInstallments: Number(v.blockNewLoanAfterInstallments) > 0 ? Number(v.blockNewLoanAfterInstallments) : null,
        productPricingMode: productOffice ? v.productPricingMode : 'CONSTANT',
      });
      notify.success(t('routes.createdOk'));
      navigate('/admin/routes');
    } catch (error) {
      notify.error(apiErrorMessage(error, t('routes.createError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPage narrow>
      <PageHeader
        title={t('routes.newTitle')}
        description={t('routes.formSubtitle')}
        breadcrumbs={[{ label: t('routes.title'), href: '#/admin/routes' }, { label: t('routes.newTitle') }]}
        helpAction={<ScreenTourButton />}
      />
      {inherited && (
        <Box data-tour="route-new-inherited">
          <Notice
            severity="info"
            action={
              <Button component={Link} to="/admin/route-defaults" size="small" color="inherit">
                {t('routes.defaults.inheritedLink')}
              </Button>
            }
          >
            {t('routes.defaults.inheritedNote')}
          </Notice>
        </Box>
      )}
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <RouteFormSections
          values={values}
          errors={errors}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
          offices={offices}
          users={users}
          productOffice={productOffice}
        />
        <FormActions cancelTo="/admin/routes" saving={saving} allowed={canCreate} deniedTooltip={t('common.noPermissionCreate')} />
      </Box>
    </AdminPage>
  );
};

export default RouteNew;
